import Link from 'next/link';
import { cn } from '@/lib/utils';
import { BrowserShot } from './shots';

/**
 * Shared section primitives for the products section. Accent is always passed
 * explicitly (one accent per product page, locked — see DESIGN.md).
 */

/* Section heading: optional eyebrow (restrained), Six Caps lowercase title. */
export function SectionHead({
  kicker,
  title,
  lede,
  accent,
  className,
  titleClassName,
}: {
  kicker?: string;
  title: string;
  lede?: string;
  accent: string;
  className?: string;
  titleClassName?: string;
}) {
  return (
    <div className={cn('max-w-3xl', className)}>
      {kicker ? (
        <span className="eyebrow" style={{ color: accent }}>
          {kicker}
        </span>
      ) : null}
      <h2
        className={cn(
          'mt-5 font-six-caps text-5xl lowercase leading-[0.92] text-white sm:text-6xl md:text-7xl',
          titleClassName,
        )}
      >
        {title}
      </h2>
      {lede ? (
        <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/65 md:text-lg">
          {lede}
        </p>
      ) : null}
    </div>
  );
}

/* CTA row: one accent-filled primary, ghost/live secondary. */
export function CtaRow({
  primary,
  secondary,
  accent,
  className,
}: {
  primary: { label: string; href: string };
  secondary?: { label: string; href: string; external?: boolean };
  accent: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-4', className)}>
      <Link
        href={primary.href}
        data-cursor-label="Go"
        className="group inline-flex items-center gap-3 rounded-full px-7 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0"
        style={{ backgroundColor: accent }}
      >
        {primary.label}
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        >
          <line x1="7" y1="17" x2="17" y2="7" />
          <polyline points="7 7 17 7 17 17" />
        </svg>
      </Link>
      {secondary ? (
        <Link
          href={secondary.href}
          {...(secondary.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          data-cursor-label="Open"
          className="inline-flex items-center gap-2.5 rounded-full border border-white/20 px-7 py-3.5 text-sm font-medium text-white/80 transition-colors duration-300 hover:border-white/50 hover:text-white"
        >
          {secondary.label}
          {secondary.external ? (
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="7" y1="17" x2="17" y2="7" />
              <polyline points="7 7 17 7 17 17" />
            </svg>
          ) : null}
        </Link>
      ) : null}
    </div>
  );
}

/* Mono pill announcing the live product URL. */
export function LivePill({
  href,
  label,
  accent,
}: {
  href: string;
  label: string;
  accent: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55 transition-colors duration-300 hover:text-white"
    >
      <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
        <span
          className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
          style={{ backgroundColor: accent }}
        />
        <span
          className="relative inline-flex h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: accent }}
        />
      </span>
      {label}
    </a>
  );
}

/* Stat grid: Six Caps numerals on a hairline grid. Real numbers only. */
export function StatGrid({
  stats,
  accent,
  className,
}: {
  stats: Array<{ value: string; label: string }>;
  accent: string;
  className?: string;
}) {
  const cols =
    stats.length <= 4
      ? 'md:grid-cols-2 lg:grid-cols-4'
      : 'md:grid-cols-3 lg:grid-cols-6';
  return (
    <dl
      className={cn(
        'grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10',
        cols,
        className,
      )}
    >
      {stats.map((stat) => (
        <div key={stat.label} className="flex flex-col gap-3 bg-[#0b0f0e] p-6 md:p-7">
          <dd className="font-six-caps text-5xl leading-none text-white md:text-6xl">
            {stat.value}
          </dd>
          <dt className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
            <span
              className="h-px w-4 shrink-0"
              style={{ backgroundColor: accent }}
              aria-hidden="true"
            />
            {stat.label}
          </dt>
        </div>
      ))}
    </dl>
  );
}

/* The one marquee allowed per page. Mono items, ✦ separators, accent ticks. */
export function ProductMarquee({
  items,
  accent,
  duration = 36,
  className,
}: {
  items: string[];
  accent: string;
  /** Seconds for one full loop. */
  duration?: number;
  className?: string;
}) {
  return (
    <div
      className={cn('marquee-container border-y border-white/10 py-5', className)}
      aria-hidden="true"
    >
      <div className="marquee-track" style={{ animationDuration: `${duration}s` }}>
        {[0, 1].map((dup) => (
          <div key={dup} className="flex shrink-0 items-center">
            {items.map((item) => (
              <span
                key={`${dup}-${item}`}
                className="flex items-center font-mono text-xs uppercase tracking-[0.3em] text-white/45"
              >
                <span className="px-7">{item}</span>
                <span style={{ color: accent }} aria-hidden="true">
                  ✦
                </span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Numbered editorial rows (replaces bullet lists). */
export function FeatureRows({
  rows,
  accent,
  className,
}: {
  rows: Array<{ title: string; body: string }>;
  accent: string;
  className?: string;
}) {
  return (
    <div className={cn('border-t border-white/10', className)}>
      {rows.map((row, i) => (
        <div
          key={row.title}
          className="grid grid-cols-[auto_1fr] items-baseline gap-x-6 gap-y-3 border-b border-white/10 py-7 md:grid-cols-[auto_minmax(0,1fr)_minmax(0,1.3fr)] md:gap-x-12"
        >
          <span
            className="font-mono text-[11px] tracking-[0.2em]"
            style={{ color: accent }}
            aria-hidden="true"
          >
            {String(i + 1).padStart(2, '0')}
          </span>
          <h3 className="text-xl font-light leading-snug text-white md:text-2xl">
            {row.title}
          </h3>
          <p className="col-span-2 max-w-xl text-sm font-light leading-relaxed text-white/55 md:col-span-1 md:text-base">
            {row.body}
          </p>
        </div>
      ))}
    </div>
  );
}

/* Horizontal snap rail of real screenshots with mono captions. */
export function GalleryRail({
  shots,
  accent,
  className,
}: {
  shots: Array<{ src: string; alt: string; caption: string }>;
  accent: string;
  className?: string;
}) {
  return (
    <div
      className={cn('pv-rail -mx-6 overflow-x-auto px-6 md:-mx-12 md:px-12 lg:-mx-24 lg:px-24', className)}
    >
      <div className="flex snap-x snap-mandatory gap-6 pb-2">
        {shots.map((shot, i) => (
          <figure key={shot.src} className="w-[84vw] shrink-0 snap-start sm:w-[30rem] md:w-[36rem]">
            <BrowserShot src={shot.src} alt={shot.alt} accent={accent} aspect="aspect-[16/10]" />
            <figcaption className="mt-4 flex items-baseline gap-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
              <span style={{ color: accent }} aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              {shot.caption}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

/* Final CTA band: ghost Six Caps watermark behind, one intent only. */
export function DemoBand({
  ghost,
  title,
  sub,
  primary,
  secondary,
  accent,
  note,
}: {
  ghost: string;
  title: string;
  sub: string;
  primary: { label: string; href: string };
  secondary?: { label: string; href: string; external?: boolean };
  accent: string;
  /** Small mono note under the CTAs (demo logins, live URLs). */
  note?: string;
}) {
  return (
    <section className="relative overflow-hidden border-t border-white/10 py-28 md:py-36">
      <span
        aria-hidden="true"
        className="pv-ghost pointer-events-none absolute -bottom-8 left-1/2 w-full -translate-x-1/2 select-none text-center font-six-caps text-[24vw] uppercase leading-none"
      >
        {ghost}
      </span>
      <div className="relative">
        <span className="eyebrow" style={{ color: accent }}>
          Book a demo
        </span>
        <h2 className="mt-6 max-w-4xl font-six-caps text-6xl lowercase leading-[0.9] text-white sm:text-7xl md:text-8xl">
          {title}
        </h2>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/65 md:text-lg">
          {sub}
        </p>
        <CtaRow primary={primary} secondary={secondary} accent={accent} className="mt-10" />
        {note ? (
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
            {note}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/* Cross-sell footer: the other two products as big link rows. */
export function NextProducts({
  items,
  className,
}: {
  items: Array<{ slug: string; name: string; tagline: string; accent: string }>;
  className?: string;
}) {
  return (
    <nav aria-label="Other products" className={cn('border-t border-white/10', className)}>
      {items.map((item) => (
        <Link
          key={item.slug}
          href={`/products/${item.slug}`}
          data-cursor-label="View"
          className="group flex items-baseline justify-between gap-6 border-b border-white/10 py-8 md:py-10"
        >
          <span className="flex items-baseline gap-6 md:gap-10">
            <span
              className="font-six-caps text-5xl lowercase leading-none text-white/80 transition-colors duration-300 group-hover:text-white sm:text-6xl md:text-7xl"
            >
              {item.name}
            </span>
            <span className="hidden max-w-xs text-sm font-light text-white/45 md:inline">
              {item.tagline}
            </span>
          </span>
          <span
            className="font-mono text-[11px] uppercase tracking-[0.25em] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
            style={{ color: item.accent }}
            aria-hidden="true"
          >
            →
          </span>
        </Link>
      ))}
    </nav>
  );
}

/* Floating mono chip used around hero visuals (desktop only). */
export function FloatChip({
  children,
  accent,
  className,
  alt = false,
  dot = false,
}: {
  children: React.ReactNode;
  accent: string;
  className?: string;
  /** Use the 7s alternate float rhythm. */
  alt?: boolean;
  /** Show an accent dot before the label. */
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        'pointer-events-none absolute z-20 hidden items-center gap-2 rounded-full border border-white/15 bg-[#0b0f0e]/90 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 shadow-[0_16px_50px_-12px_rgba(0,0,0,0.7)] backdrop-blur-sm lg:inline-flex',
        alt ? 'float-animation-alt' : 'float-animation',
        className,
      )}
    >
      {dot ? (
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} aria-hidden="true" />
      ) : null}
      {children}
    </span>
  );
}

/* Radial accent glow placed behind hero visuals. */
export function AccentGlow({
  accent,
  className,
  opacity = 0.22,
}: {
  accent: string;
  className?: string;
  opacity?: number;
}) {
  return (
    <div
      aria-hidden="true"
      className={cn('pointer-events-none absolute rounded-full blur-[110px]', className)}
      style={{ backgroundColor: accent, opacity }}
    />
  );
}
